import {GameLogState, PlayerColor, GameState, NestedPartial} from '@colonist/game-states'
import {SocketActionController} from '../socket/SocketActionController'
import {BuildGameConfigurations, ReplayInitiationData} from '@colonist/game-base-types'
import {filterGameStateForPerspectives, filterGameLogsForPerspectives, diffDataObjects, applyDiffs, squashDiffs} from '@colonist/game-state-types'
import {UIGameEndController} from '../ui_game/ui_game_end_screen/UIGameEndController'
import {KeyBindController, ShortcutLocation} from '../KeyBindController'
import {UIGameManager} from '../ui_game/UIGameManager'
import {uiGameManager} from '../EntryShared'
import {MathUtils} from '../../../../shared/utils/MathUtils'
import {AnalyticEventCategory, AnalyticEventType, ReplayAccessMethod} from '../../../../shared/entity_datas/EntityDatas'
import {getReplayAccessMethod} from './ReplayUrlUtilities'
import {getHighlightsFromEventHistory} from './ReplayHighlightsUtilities'
import {UIReplayShareController} from './UIReplayShareController'
import {UIErrorHandler} from '../errors/UIErrorHandler'
import {ErrorReplay} from '../../../../shared/errors/ErrorsReplay'
import {CA} from '../analytics/Analytics'

export class UIReplayController {
    readonly initialStateIndex = -1
    readonly replayAccessMethod: ReplayAccessMethod

    private readonly socketActionController: SocketActionController
    private readonly uiGameManager: UIGameManager
    private readonly isLiveGame: boolean
    readonly replayData: ReplayInitiationData

    readonly actionIndexForEndgame: number
    private readonly reverseGameStateChanges: NestedPartial<GameState>[]

    private actionIndex: number
    private buildGameConfigurations: BuildGameConfigurations
    private endGameScreen: UIGameEndController | undefined

    constructor(replayInitiationData: ReplayInitiationData, socketActionController: SocketActionController, isLiveGame: boolean, startingActionIndex?: number) {
        this.socketActionController = socketActionController
        this.uiGameManager = uiGameManager
        this.isLiveGame = isLiveGame
        this.replayData = replayInitiationData
        this.replayAccessMethod = getReplayAccessMethod(window.location.href) // TODO fix this, incorrect logic for mobile

        const currentAction = startingActionIndex ?? this.initialStateIndex

        this.filterGameHistory()
        this.actionIndexForEndgame = this.replayData.eventHistory.events.length - 1
        this.reverseGameStateChanges = this.getReverseGameStateChanges()

        this.actionIndex = this.initialStateIndex

        this.buildGameConfigurations = this.getBuildGameConfigurations(this.replayData.playerPerspective, this.isLiveGame)
        this.uiGameManager.canvasController.buildGame()

        if(this.isLiveGame) {
            this.buildGameConfigurations.gameState.gameChatState = this.uiGameManager.gameController['stateController']['state'].gameChatState
        }

        this.socketActionController.onBuildGame(this.buildGameConfigurations)

        this.updateGameLogPosition()
        this.setAction(currentAction, false)
        this.bindKeys()
        this.createEndGameScreen()
        if(this.actionIndex == this.actionIndexForEndgame) this.showEndGameScreen()
        else this.hideEndGameScreen()
        this.setupReplayControls()
    }

    private setupReplayControls() {
        const highlightData = getHighlightsFromEventHistory(this.replayData.eventHistory)

        this.uiGameManager.gameStore.getState().gameReplay.setReplayControls({
            onPreviousTurn: () => this.previousTurn(),
            onPreviousAction: () => this.previousAction(),
            onNextAction: () => this.nextAction(),
            onNextTurn: () => this.nextTurn(),
            onChangePerspective: (color: PlayerColor) => this.onClickChangePerspective(color),
            currentPlayerPerspective: this.playerPerspective,
            onShare: () => UIReplayShareController.shareOrCopyLink(this.uiGameManager.gameController, this),
            numberOfActions: this.replayData.eventHistory.events.length,
            highlightData: highlightData,
            onChangeActionIndex: (actionIndex: number) => this.setAction(actionIndex, false),
            onClickLog: (logIndex: number) => this.clickedGameLogWithIndex(logIndex),
        })
    }

    private bindKeys() {
        const keyBindDelayInMS = 0
        const withoutShift = false
        KeyBindController.bindKey('ArrowLeft', () => this.previousAction(), () => true, ShortcutLocation.InGame, withoutShift, keyBindDelayInMS)
        KeyBindController.bindKey('ArrowRight', () => this.nextAction(), () => true, ShortcutLocation.InGame, withoutShift, keyBindDelayInMS)

        const withShift = true
        KeyBindController.bindKey('ArrowLeft', () => this.previousTurn(), () => true, ShortcutLocation.InGame, withShift, keyBindDelayInMS)
        KeyBindController.bindKey('ArrowRight', () => this.nextTurn(), () => true, ShortcutLocation.InGame, withShift, keyBindDelayInMS)
    }

    private filterGameHistory(): void {
        for(const event of this.replayData.eventHistory.events) {
            if(event.stateChange.tradeState != undefined) {
                // eslint-disable-next-line
                // @ts-ignore
                delete event.stateChange.tradeState
            }

            // Filter out people toggling special build phase. Only show when they enter
            if(event.stateChange.mechanicSpecialBuildPhaseState?.playerStates != undefined) {
                delete event.stateChange.mechanicSpecialBuildPhaseState.playerStates
                if(Object.keys(event.stateChange.mechanicSpecialBuildPhaseState).length == 0) delete event.stateChange.mechanicSpecialBuildPhaseState
            }

            // Ignore chat msgs on the end game screen on a live game so players can still communicate
            if(this.isLiveGame) {
                if(event.stateChange.gameChatState != undefined) {
                    delete event.stateChange.gameChatState
                }
            }
        }

        this.replayData.eventHistory.events = this.replayData.eventHistory.events.filter((event) => Object.keys(event.stateChange).length !== 0)
    }

    private getReverseGameStateChanges(): NestedPartial<GameState>[] {
        const reverseGameEvents: NestedPartial<GameState>[] = []
        const currentState = structuredClone(this.replayData.eventHistory.initialState)
        const futureState = structuredClone(this.replayData.eventHistory.initialState)
        for(const [actionIndex, event] of this.replayData.eventHistory.events.entries()) {
            const forwardStateChange = this.replayData.eventHistory.events[actionIndex].stateChange
            applyDiffs(futureState, [forwardStateChange])

            const reverseGameStateChange = diffDataObjects(futureState, currentState)
            if(reverseGameStateChange == undefined) throw new Error('Error loading replay data')

            reverseGameEvents[actionIndex - 1] = reverseGameStateChange

            applyDiffs(currentState, [forwardStateChange])
        }

        return reverseGameEvents
    }

    private getBuildGameConfigurations(myColor: PlayerColor, isAfterLiveGame: boolean): BuildGameConfigurations {
        const gameState = structuredClone(this.replayData.eventHistory.initialState)
        gameState.gameLogState = this.getFullGameLogForPlayer(myColor)

        this.buildGameConfigurations = {
            gameState: gameState,
            playOrder: this.replayData.playOrder,
            playerColor: myColor,
            playerUserStates: this.replayData.playerUserStates,
            gameDetails: this.replayData.gameDetails,
            gameSettings: this.replayData.gameSettings,
            timeLeftInState: 0,
            isReplay: true,
            isAfterLiveGame: isAfterLiveGame,
        }
        return this.buildGameConfigurations
    }

    get playerPerspective(): PlayerColor {
        return this.buildGameConfigurations.playerColor
    }

    private isPlayerPerspectiveAvailable(playerColor: PlayerColor) {
        return this.replayData.eventHistory.eventsPerspectives.includes(playerColor)
    }

    private onClickChangePerspective(playerColor: PlayerColor) {
        void CA.sendAnalyticEvent({
            type: AnalyticEventType.ReplayClickedChangePerspective,
            category: AnalyticEventCategory.Replay,
            floatValue: this.replayAccessMethod,
        })

        const isPerspectiveAvailable = this.isPlayerPerspectiveAvailable(playerColor)
        if(!isPerspectiveAvailable) return UIErrorHandler.handleError(new ErrorReplay.NoMembershipTryingToChangeReplayPerspective())

        this.changePlayerPerspective(playerColor)
    }

    private changePlayerPerspective(playerColor: PlayerColor) {
        this.uiGameManager.resetGameUIToRebuildOnReconnect()

        this.buildGameConfigurations.playerColor = playerColor

        const newPlayerGameState = structuredClone(this.replayData.eventHistory.initialState)
        for(let currentActionIndex = 0; currentActionIndex <= this.actionIndex; currentActionIndex++) {
            const singleStateDiff = this.replayData.eventHistory.events[currentActionIndex].stateChange
            const filteredStateDiff = filterGameStateForPerspectives(singleStateDiff, [this.playerPerspective])
            applyDiffs(newPlayerGameState, [filteredStateDiff])
        }

        this.buildGameConfigurations.gameState = {
            ...newPlayerGameState,
            gameLogState: this.getFullGameLogForPlayer(this.playerPerspective),
            gameChatState: this.buildGameConfigurations.gameState.gameChatState,
        }

        this.socketActionController.onBuildGame(this.buildGameConfigurations)
        this.setupReplayControls()
        this.updateGameLogPosition()
        this.resetEndGameScreen()
        this.createEndGameScreen()
        if(this.actionIndex == this.actionIndexForEndgame) this.showEndGameScreen()
        else this.hideEndGameScreen()

        this.uiGameManager.gameStore.getState().gameReplay.setActionIndex(this.actionIndex)
    }

    previousAction() {
        this.setAction(this.actionIndex - 1, false)
    }

    nextAction() {
        this.setAction(this.actionIndex + 1, true)
    }

    previousTurn() {
        this.setAction(this.findPreviousTurn(), false)
    }

    nextTurn() {
        this.setAction(this.findNextTurn(), false)
    }

    getCurrentAction(): number {
        return this.actionIndex
    }

    private findNextTurn(): number {
        for(let i = this.actionIndex + 1; i < this.replayData.eventHistory.events.length; i++) {
            if((this.replayData.eventHistory.events[i].stateChange.currentState?.completedTurns) != null) return i
        }
        return this.replayData.eventHistory.events.length - 1
    }

    private findPreviousTurn(): number {
        for(let i = this.actionIndex - 1; i >= 0; i--) {
            if((this.replayData.eventHistory.events[i].stateChange.currentState?.completedTurns) != null) return i
        }
        return this.initialStateIndex
    }

    private setAction(actionIndex: number, playAnimations: boolean) {
        actionIndex = MathUtils.clamp(actionIndex, this.initialStateIndex, this.actionIndexForEndgame)
        if(actionIndex == this.actionIndex) return

        actionIndex > this.actionIndex ? this.advanceToAction(actionIndex, playAnimations) : this.rewindToAction(actionIndex, playAnimations)
        this.actionIndex = actionIndex
        this.updateGameLogPosition()
        this.actionIndex == this.actionIndexForEndgame ? this.showEndGameScreen() : this.hideEndGameScreen()
        this.uiGameManager.gameStore.getState().gameReplay.setActionIndex(actionIndex)
    }

    private advanceToAction(actionIndex: number, playAnimations: boolean) {
        const forwardDiffs: NestedPartial<GameState>[] = []
        for(let currentActionIndex = this.actionIndex + 1; currentActionIndex <= actionIndex; currentActionIndex++) {
            const stateDiff = this.replayData.eventHistory.events[currentActionIndex].stateChange
            const filteredStateDiff = filterGameStateForPerspectives(stateDiff, [this.playerPerspective])
            forwardDiffs.push(filteredStateDiff)
        }
        const squashForwardDiff = squashDiffs(forwardDiffs)
        delete squashForwardDiff.gameLogState

        this.socketActionController.gameStateUpdated({diff: squashForwardDiff, timeLeftInState: 0}, playAnimations)
    }

    private rewindToAction(actionIndex: number, playAnimations: boolean) {
        const rewindDiffs: NestedPartial<GameState>[] = []
        for(let currentActionIndex = this.actionIndex - 1; currentActionIndex >= actionIndex; currentActionIndex--) {
            const stateDiff = this.reverseGameStateChanges[currentActionIndex]
            const filteredStateDiff = filterGameStateForPerspectives(stateDiff, [this.playerPerspective])
            rewindDiffs.push(filteredStateDiff)
        }
        const squashRewindDiff = squashDiffs(rewindDiffs)
        delete squashRewindDiff.gameLogState

        this.socketActionController.gameStateUpdated({diff: squashRewindDiff, timeLeftInState: 0}, playAnimations)
    }

    private showEndGameScreen() {
        this.uiGameManager.mapController.setInteractionEnabled(false)

        if(this.endGameScreen != undefined) {
            this.uiGameManager.gameStore.getState().gameEnd.setIsVisible(true)
            return
        }

        this.createEndGameScreen()
    }

    createEndGameScreen() {
        if(this.replayData.eventHistory.endGameState == undefined) return

        this.endGameScreen = this.socketActionController.onGameEndState({
            endGameState: this.replayData.eventHistory.endGameState,
            isReplayAvailable: true,
            rankedUserStates: this.replayData.rankedUserStates,
        }, this.isLiveGame)
    }

    hideEndGameScreen() {
        if(this.endGameScreen == undefined) return

        this.uiGameManager.mapController.setInteractionEnabled(true)
        this.uiGameManager.gameStore.getState().gameEnd.setIsVisible(false)
    }

    private resetEndGameScreen() {
        if(this.endGameScreen == undefined) return

        this.endGameScreen.destroy()
        this.endGameScreen = undefined
    }

    destroy() {
        KeyBindController.instance.clearAllKeyBinds()

        this.resetEndGameScreen()

        this.uiGameManager.mapController.setInteractionEnabled(true)
    }

    clickedGameLogWithIndex(gameLogIndex: number) {
        const actionIndex = this.getActionIndexByGameLogIndex(gameLogIndex)
        if(actionIndex == undefined) return
        this.setAction(actionIndex, false)
    }

    private getActionIndexByGameLogIndex(gameLogIndex: number): number | undefined {
        if(gameLogIndex == 0) return this.initialStateIndex
        for(let actionIndex = 0; actionIndex < this.replayData.eventHistory.events.length; actionIndex++) {
            const gameLogState = this.replayData.eventHistory.events[actionIndex].stateChange.gameLogState
            for(const key in gameLogState) {
                const index = Number(key)
                if(index == gameLogIndex) return actionIndex
            }
        }
        return undefined
    }

    private getLastGameLogIndexByActionIndex(): number | undefined {
        const actionIndex = this.actionIndex
        if(actionIndex == this.initialStateIndex) return 0
        if(actionIndex < 0) return undefined

        const gameLogState = structuredClone(this.replayData.eventHistory.events[actionIndex].stateChange.gameLogState)
        if(gameLogState == undefined) return

        filterGameLogsForPerspectives(gameLogState, [this.buildGameConfigurations.playerColor])

        const actionIndices = Object.keys(gameLogState)
        if(actionIndices.length == 0) return undefined

        return Number(actionIndices[actionIndices.length - 1])
    }

    private getFullGameLogForPlayer(player: PlayerColor): GameLogState {
        const gameLogState: GameLogState = structuredClone(this.replayData.eventHistory.initialState.gameLogState)
        for(const event of this.replayData.eventHistory.events) {
            const gameLogData = event.stateChange.gameLogState

            if(gameLogData == undefined) continue

            const gameLogDataClone = structuredClone(gameLogData)

            filterGameLogsForPerspectives(gameLogDataClone, [player])

            Object.assign(gameLogState, gameLogDataClone)
        }

        return gameLogState
    }

    private updateGameLogPosition() {
        const gameLogIndex = this.getLastGameLogIndexByActionIndex()
        if(gameLogIndex == undefined) return

        this.uiGameManager.gameStore.getState().gameReplay.setFocusedLogIndex(gameLogIndex)
    }
}
